import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { OrderType, OrderStatus, DeliveryPlatform } from '@/types/order'
import { CustomerInfo, DeliveryInfo, PickupInfo, DineInInfo } from '@/types/customer'
import { PaymentMethod } from '@/types/order'

interface OrderState {
  orderType: OrderType | null
  selectedLocationId: string | null
  deliveryPlatform: DeliveryPlatform | null
  customerInfo: CustomerInfo | null
  deliveryInfo: DeliveryInfo | null
  pickupInfo: PickupInfo | null
  dineInInfo: DineInInfo | null
  paymentMethod: PaymentMethod | null
  currentStep: number
  orderId: string | null
  orderNumber: string | null
  trackingCode: string | null
  orderStatus: OrderStatus
  estimatedTime: string | null
  specialInstructions: string
  error: string | null
}

const VALID_ORDER_STATUSES: OrderStatus[] = [
  'idle', 'pending', 'validating', 'submitting', 'confirmed', 'preparing',
  'ready', 'out-for-delivery', 'delivered', 'completed', 'cancelled', 'error',
]

function isValidUUID(value: unknown): boolean {
  return typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
}

function loadPersistedOrder(): Partial<OrderState> {
  try {
    const stored = localStorage.getItem('restohub-order')
    if (!stored) return {}

    const parsed = JSON.parse(stored)
    if (typeof parsed !== 'object' || parsed === null) return {}

    const result: Partial<OrderState> = {}

    if (isValidUUID(parsed.orderId)) result.orderId = parsed.orderId
    if (typeof parsed.orderNumber === 'string' && parsed.orderNumber.length <= 20) result.orderNumber = parsed.orderNumber
    if (typeof parsed.trackingCode === 'string' && parsed.trackingCode.length <= 50) result.trackingCode = parsed.trackingCode
    if (typeof parsed.orderStatus === 'string' && VALID_ORDER_STATUSES.includes(parsed.orderStatus)) result.orderStatus = parsed.orderStatus as OrderStatus
    if (typeof parsed.estimatedTime === 'string' && !isNaN(Date.parse(parsed.estimatedTime))) result.estimatedTime = parsed.estimatedTime
    if (typeof parsed.orderType === 'string' && ['delivery', 'pickup', 'dine-in'].includes(parsed.orderType)) result.orderType = parsed.orderType as OrderType

    return result
  } catch {
    try { localStorage.removeItem('restohub-order') } catch { /* ignore */ }
    return {}
  }
}

function persistOrder(state: OrderState) {
  try {
    localStorage.setItem('restohub-order', JSON.stringify({
      orderId: state.orderId,
      orderNumber: state.orderNumber,
      trackingCode: state.trackingCode,
      orderStatus: state.orderStatus,
      estimatedTime: state.estimatedTime,
      orderType: state.orderType,
    }))
  } catch { /* ignore */ }
}

const baseState: OrderState = {
  orderType: null,
  selectedLocationId: null,
  deliveryPlatform: null,
  customerInfo: null,
  deliveryInfo: null,
  pickupInfo: null,
  dineInInfo: null,
  paymentMethod: null,
  currentStep: 0,
  orderId: null,
  orderNumber: null,
  trackingCode: null,
  orderStatus: 'idle',
  estimatedTime: null,
  specialInstructions: '',
  error: null,
}

const initialState: OrderState = { ...baseState, ...loadPersistedOrder() }

export const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    setOrderType: (state, action: PayloadAction<OrderType>) => {
      state.orderType = action.payload
      // Keep delivery/pickup info when switching between order types
      // This allows users to switch back and forth without losing their data
    },

    setSelectedLocationId: (state, action: PayloadAction<string | null>) => {
      state.selectedLocationId = action.payload
    },

    setDeliveryPlatform: (state, action: PayloadAction<DeliveryPlatform>) => {
      state.deliveryPlatform = action.payload
    },

    setCustomerInfo: (state, action: PayloadAction<CustomerInfo>) => {
      state.customerInfo = action.payload
    },

    setDeliveryInfo: (state, action: PayloadAction<DeliveryInfo>) => {
      state.deliveryInfo = action.payload
    },

    setPickupInfo: (state, action: PayloadAction<PickupInfo>) => {
      state.pickupInfo = action.payload
    },

    setDineInInfo: (state, action: PayloadAction<DineInInfo>) => {
      state.dineInInfo = action.payload
    },

    setPaymentMethod: (state, action: PayloadAction<PaymentMethod>) => {
      state.paymentMethod = action.payload
    },

    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload
    },

    nextStep: (state) => {
      state.currentStep += 1
    },

    previousStep: (state) => {
      state.currentStep = Math.max(0, state.currentStep - 1)
    },

    setOrderStatus: (state, action: PayloadAction<OrderStatus>) => {
      state.orderStatus = action.payload
      persistOrder(state)
    },

    setOrderId: (state, action: PayloadAction<string>) => {
      state.orderId = action.payload
      persistOrder(state)
    },

    setOrderNumber: (state, action: PayloadAction<string>) => {
      state.orderNumber = action.payload
      persistOrder(state)
    },

    setTrackingCode: (state, action: PayloadAction<string>) => {
      state.trackingCode = action.payload
      persistOrder(state)
    },

    setEstimatedTime: (state, action: PayloadAction<string>) => {
      state.estimatedTime = action.payload
      persistOrder(state)
    },

    setSpecialInstructions: (state, action: PayloadAction<string>) => {
      state.specialInstructions = action.payload
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },

    resetOrder: () => {
      try { localStorage.removeItem('restohub-order') } catch { /* ignore */ }
      return baseState
    },

    resetCheckoutProgress: (state) => {
      state.currentStep = 0
      state.orderStatus = 'idle'
      state.error = null
    },
  },
})

export const {
  setOrderType,
  setSelectedLocationId,
  setDeliveryPlatform,
  setCustomerInfo,
  setDeliveryInfo,
  setPickupInfo,
  setDineInInfo,
  setPaymentMethod,
  setCurrentStep,
  nextStep,
  previousStep,
  setOrderStatus,
  setOrderId,
  setOrderNumber,
  setTrackingCode,
  setEstimatedTime,
  setSpecialInstructions,
  setError,
  resetOrder,
  resetCheckoutProgress,
} = orderSlice.actions

export default orderSlice.reducer
